import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteGroupMutation, useUpdateGroupMutation } from '../../services/api';
import DeleteConfirmDialogBox from './DeleteConfirmDialogBox';
import EditGroupDialogBox from './EditGroupDialogBox';

const DeviceGroupsTable = ({ deviceGroups = [], refetch }) => {
    const theme = useTheme();

    // API
    const [deleteGroup, { isLoading: isDeleting }] = useDeleteGroupMutation();
    const [updateGroup, { isLoading: isUpdating }] = useUpdateGroupMutation();

    const [groups, setGroups] = useState(deviceGroups);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [groupToEdit, setGroupToEdit] = useState(null);

    useEffect(() => {
        setGroups(deviceGroups);
    }, [deviceGroups]);

    // Open dialog box
    const openConfirmDialog = (group) => {
        setGroupToDelete(group);
        setConfirmOpen(true);
    };

    // Close dialog box
    const closeConfirmDialog = () => {
        setConfirmOpen(false);
        setGroupToDelete(null);
    };

    // Handle delete
    const handleDeleteConfirm = async () => {
        try {
            await deleteGroup(groupToDelete._id).unwrap();
            refetch();
            closeConfirmDialog();
        } catch (error) {
            console.error('Failed to delete group:', error);
        }
    };

    // Open edit dialog
    const openEditDialog = (group) => {
        setGroupToEdit(group);
        setEditDialogOpen(true);
    };

    // Close edit dialog
    const closeEditDialog = () => {
        setEditDialogOpen(false);
        setGroupToEdit(null);
    };

    // Hnadle edit
    const handleEditSave = async (updatedGroup) => {
        try {
            await updateGroup({
                id: updatedGroup._id,
                ...updatedGroup,
            }).unwrap();
            closeEditDialog();
            refetch();
        } catch (err) {
            console.error('Update failed:', err);
        }
    };

    return (
        <>
            <Box
                p={4}
                borderRadius={5}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.secondary.main,
                    width: '100%',
                    mt: 2,
                    boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                }}
            >
                <TableContainer component={Paper} sx={{ boxShadow: 'none', backgroundColor: 'transparent' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>Name</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Sites</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Devices</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Users</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groups.length > 0 ? (
                                groups.map((row) => (
                                    <TableRow key={row._id}>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.sites?.map((site) => site.name).join(', ')}</TableCell>
                                        <TableCell>{row.devices?.map((device) => device.name).join(', ')}</TableCell>
                                        <TableCell>{row.users?.map((user) => user.name).join(', ')}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                color="primary"
                                                onClick={() => openEditDialog(row)}
                                                sx={{
                                                    '&:focus': {
                                                        outline: 'none',
                                                    },
                                                    '&:focus-visible': {
                                                        outline: 'none',
                                                    },
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                onClick={() => openConfirmDialog(row)}
                                                sx={{
                                                    '&:focus': {
                                                        outline: 'none',
                                                    },
                                                    '&:focus-visible': {
                                                        outline: 'none',
                                                    },
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No Device Groups Available
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <EditGroupDialogBox open={editDialogOpen} onClose={closeEditDialog} group={groupToEdit} onSave={handleEditSave} isUpdating={isUpdating} />

            <DeleteConfirmDialogBox open={confirmOpen} handleClose={closeConfirmDialog} handleConfirm={handleDeleteConfirm} isDeleting={isDeleting} />
        </>
    );
};

export default DeviceGroupsTable;
